import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getComprobantesContableAction,
  getComprobantesModuloContableAction,
} from "redux/actions/contableComprobantesAction";
import { fetchModuloContable } from "redux/actions/contableActions";
import { getAnioActual, getRUCEmpresa } from "redux/actions/userActions";

const API_GET_VOUCHER_AUTHORIZED = "/comprobantes/autorizados/busqueda/";
const DASHBOARD_VOUCHERS_PAGE_SIZE = 15;
const DASHBOARD_VOUCHERS_SORT = "idAsignacionComprobanteElectronico,desc";

const EMPTY_ADVANCED_FILTERS = {
  fechaInicio: null,
  fechaFin: null,
  secuencial: null,
  parametroReceptor: null,
};

const MESES_LABEL = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

const getPeriodoLabel = (anio, mes) => {
  if (!anio || !mes) {
    const today = new Date();
    return `${today.toLocaleString("es-ES", { month: "long" })} ${today.getFullYear()}`;
  }
  return `${MESES_LABEL[mes - 1]} ${anio}`;
};

const buildStats = (data, anio, mes) => {
  if (!data) return [];
  if (Array.isArray(data)) return data.length > 0 ? data : [];

  return [
    {
      title: "Ingresos del Mes",
      value: data.ingresosMes,
      detail: getPeriodoLabel(anio, mes),
      tone: "success",
    },
    {
      title: "Gastos del Mes",
      value: data.valorGatos,
      detail: `${data.totalGastos} comprobantes`,
      tone: "danger",
    },
    {
      title: "Utilidad Neta",
      value: data.utilidadNeta,
      detail: "Ingresos - Gastos",
      tone: "info",
    },
    {
      title: "Facturas Emitidas",
      value: data.totalFacturas,
      detail: "este mes",
      tone: "success",
    },
    {
      title: "Por cobrar",
      value: data.porCobrarValor,
      detail: `${data.porCobrarCantidad} pendientes`,
      tone: "warning",
    },
    {
      title: "Notas de credito",
      value: data.valorNC,
      detail: `${data.totalNC} emitidas`,
      tone: "info",
    },
    {
      title: "Clientes Activos",
      value: data.numeroClientes,
      detail: "",
      tone: "success",
    },
  ];
};

/**
 * @param {Object} opciones
 * @param {string|null} opciones.voucherType
 * @param {boolean} opciones.fetchStats
 * @param {boolean} opciones.useMonthFilter Cuando es true, expone mes/anio (default: mes y anio
 *   actuales) y filtra tanto el resumen contable como la tabla de comprobantes por ese periodo.
 *   Cuando es false (default), se mantiene el comportamiento original: resumen sin filtro de
 *   periodo y comprobantes del anio completo (getAnioActual). Esto evita afectar a las vistas de
 *   Facturas y Notas de Credito, que tambien usan este hook.
 */
const useContableDashboard = ({
  voucherType = null,
  fetchStats = true,
  useMonthFilter = false,
} = {}) => {
  const dispatch = useDispatch();
  const tokenUser = useSelector((state) => state.user.tk);
  const dashboard = useSelector((state) => state.contable.dashboard);
  const authorizedVouchers = useSelector((state) => state.contableComprobantes);

  const today = new Date();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DASHBOARD_VOUCHERS_PAGE_SIZE);
  const [mes, setMes] = useState(today.getMonth() + 1);
  const [anio, setAnio] = useState(today.getFullYear());
  // Busqueda avanzada (fecha inicial/final exactas, secuencial, receptor).
  // Cuando fechaInicio/fechaFin estan seteadas, mandan sobre mes/anio en el
  // endpoint nuevo (ver getComprobantesModuloContableAction).
  const [advancedFilters, setAdvancedFiltersState] = useState(EMPTY_ADVANCED_FILTERS);

  useEffect(() => {
    if (!fetchStats || !tokenUser) return;
    dispatch(
      fetchModuloContable(
        getRUCEmpresa(tokenUser),
        useMonthFilter ? anio : undefined,
        useMonthFilter ? mes : undefined,
      ),
    ).catch(() => {});
  }, [dispatch, tokenUser, fetchStats, useMonthFilter, anio, mes]);

  const fetchVouchers = useCallback(() => {
    if (!tokenUser) return;

    if (useMonthFilter) {
      // Endpoint nuevo dedicado al modulo contable: filtra por mes/anio (o
      // por el rango de busqueda avanzada, que tiene prioridad) y devuelve
      // totalElements real (Page, no Slice).
      dispatch(
        getComprobantesModuloContableAction({
          rucEmpresa: tokenUser,
          page,
          elementos: rowsPerPage,
          anio,
          mes,
          type: voucherType,
          fechaInicio: advancedFilters.fechaInicio,
          fechaFin: advancedFilters.fechaFin,
          secuencial: advancedFilters.secuencial,
          parametroReceptor: advancedFilters.parametroReceptor,
        }),
      );
      return;
    }

    // Comportamiento legacy (año completo) para quien use el hook sin
    // useMonthFilter.
    const yearParams = new URLSearchParams(getAnioActual().replace(/^&/, ""));
    dispatch(
      getComprobantesContableAction({
        uri: API_GET_VOUCHER_AUTHORIZED,
        rucEmpresa: tokenUser,
        page,
        elementos: rowsPerPage,
        sort: DASHBOARD_VOUCHERS_SORT,
        ini: yearParams.get("ini"),
        fin: yearParams.get("fin"),
        type: voucherType,
      }),
    );
  }, [
    dispatch,
    tokenUser,
    page,
    rowsPerPage,
    voucherType,
    useMonthFilter,
    anio,
    mes,
    advancedFilters,
  ]);

  useEffect(() => {
    fetchVouchers();
  }, [fetchVouchers]);

  useEffect(() => {
    setPage(0);
  }, [voucherType]);

  const handlePageChange = (_event, newPage) => setPage(newPage);
  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleMesChange = useCallback((value) => {
    setMes(value);
    setPage(0);
  }, []);
  const handleAnioChange = useCallback((value) => {
    setAnio(value);
    setPage(0);
  }, []);
  const setAdvancedFilters = useCallback((filters) => {
    setAdvancedFiltersState({ ...EMPTY_ADVANCED_FILTERS, ...filters });
    setPage(0);
  }, []);
  const clearAdvancedFilters = useCallback(() => {
    setAdvancedFiltersState(EMPTY_ADVANCED_FILTERS);
    setPage(0);
  }, []);

  const stats = useMemo(
    () => buildStats(dashboard.data, useMonthFilter ? anio : null, useMonthFilter ? mes : null),
    [dashboard.data, useMonthFilter, anio, mes],
  );

  return {
    ...dashboard,
    authorizedVouchers,
    stats,
    page,
    rowsPerPage,
    onPageChange: handlePageChange,
    onRowsPerPageChange: handleRowsPerPageChange,
    reload: fetchVouchers,
    mes,
    anio,
    onMesChange: handleMesChange,
    onAnioChange: handleAnioChange,
    advancedFilters,
    setAdvancedFilters,
    clearAdvancedFilters,
  };
};

export default useContableDashboard;
